// Минимальный router (History API) — заменяет TanStack Router до его установки в Wave 3.
// Поддерживает: программную navigate, useLocation, useSearchParams, parametrized matchers.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export interface Location {
  pathname: string
  search: string
  hash: string
}

export interface RouterContextValue {
  location: Location
  navigate: (to: string, options?: { replace?: boolean }) => void
}

const RouterContext = createContext<RouterContextValue | null>(null)

function readLocation(): Location {
  if (typeof window === 'undefined') {
    return { pathname: '/', search: '', hash: '' }
  }
  return {
    pathname: window.location.pathname || '/',
    search: window.location.search,
    hash: window.location.hash,
  }
}

export interface RouterProviderProps {
  children: ReactNode
}

/** Корневой router-провайдер. Слушает popstate + патчит pushState/replaceState
 *  чтобы реагировать на программные переходы. */
export function RouterProvider({ children }: RouterProviderProps) {
  const [location, setLocation] = useState<Location>(() => readLocation())

  useEffect(() => {
    const onChange = () => setLocation(readLocation())
    window.addEventListener('popstate', onChange)
    window.addEventListener('dss-route-change', onChange)
    return () => {
      window.removeEventListener('popstate', onChange)
      window.removeEventListener('dss-route-change', onChange)
    }
  }, [])

  const navigate = useCallback((to: string, options: { replace?: boolean } = {}) => {
    if (options.replace) {
      window.history.replaceState({}, '', to)
    } else {
      window.history.pushState({}, '', to)
    }
    window.dispatchEvent(new Event('dss-route-change'))
  }, [])

  const value = useMemo<RouterContextValue>(() => ({ location, navigate }), [location, navigate])

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
}

export function useRouter(): RouterContextValue {
  const ctx = useContext(RouterContext)
  if (!ctx) throw new Error('useRouter must be used within <RouterProvider>')
  return ctx
}

export function useLocation(): Location {
  return useRouter().location
}

export function useNavigate(): RouterContextValue['navigate'] {
  return useRouter().navigate
}

/** Парсит location.search в URLSearchParams; реагирует на изменения роута. */
export function useSearchParams(): URLSearchParams {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

/** `<Link to="/foo">` — обычный <a>, но клик перехватывается навигатором. */
export interface LinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string
  replace?: boolean
}
export function Link({ to, replace, onClick, children, ...rest }: LinkProps) {
  const navigate = useNavigate()
  const handle = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return
    }
    event.preventDefault()
    navigate(to, { replace })
  }
  return (
    <a href={to} onClick={handle} {...rest}>
      {children}
    </a>
  )
}
